<template>
  <div class="controller-status">
    <!-- Status icon (hidden when checking, visible for other states) -->
    <div class="controller-status-icon" v-if="status !== 1">
      <!-- Online: wifi icon -->
      <i v-if="status === 2" class="fa-solid fa-wifi"></i>
      <!-- Offline: wifi icon (red) -->
      <i v-else-if="status === 3" class="fa-solid fa-wifi controller-status-offline-icon"></i>
      <!-- Unknown/Initial: question icon -->
      <i v-else-if="status === 0" class="fa-solid fa-question"></i>
    </div>
    
    <!-- Status text (always visible, changes based on state) -->
    <span class="controller-status-text">
      <span v-if="status === 1">조회중</span>
      <span v-else-if="status === 2">온라인</span>
      <span v-else-if="status === 3">오프라인</span>
      <span v-else>상태 확인</span>
    </span>
    
    <!-- Refresh icon (always visible and clickable) -->
    <button 
      class="controller-status-refresh" 
      @click="onRefreshClick"
      :disabled="status === 1"
      title="상태 확인"
    >
      <i class="fa-solid fa-arrow-rotate-right" :class="{ 'fa-spin': status === 1 }"></i>
    </button>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, getCurrentInstance, watch, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import useMqttControl from '@/common/hooks/useMqttControl';
import { MQTTTempCommand } from '@/common/constants';
import Axios, { ScheduleApi } from '@/api';

const props = defineProps({
  controllerId: {
    type: String,
    required: true,
  },
  autoCheck: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['status-changed']);

const mqttControl = reactive(useMqttControl());
const store = useStore();
const getters = computed(() => store.getters);

// Status states: 0 (unknown), 1 (checking), 2 (online), 3 (offline)
const status = ref(0);
const timerId = ref(undefined);
const schedules = ref([]);

// Check if this is a temp controller (boiler)
const isTempController = computed(() => {
  return props.controllerId.split("_").length < 2;
});

// 온도조절기는 MQTT 토픽에 하이픈(-) 앞 시리얼만 사용해야 함.
// 예) props.controllerId: "75DFIS03A612-1554"
//     MQTT: "cmnd/inshow/75DFIS03A612/TIMERGROUPENABLE1"
const mqttControllerId = computed(() => {
  if (!isTempController.value) return props.controllerId;
  const id = String(props.controllerId || '');
  return id.includes('-') ? id.split('-')[0] : id;
});

// Get boiler device for temp controllers
const boilerDevice = computed(() => {
  const controllerId = mqttControllerId.value;
  const devices = [];
  
  getters.value['home/getZones'].forEach(zone => {
    zone.groups?.forEach(group => {
      group.devices?.forEach(device => {
        if (device.controllerId === controllerId && device.type === 2) {
          devices.push(device);
        }
      });
    });
  });
  
  return devices && devices.length > 0 ? devices[devices.length - 1] : null;
});

// Watch store for status updates
const isReceivedCheckStatus = computed(() => {
  const controllers = getters.value['home/getCheckControllers'];
  const key = mqttControllerId.value;
  if (controllers[key]) {
    return controllers[key];
  }
  return 0;
});

// Watch for status changes from store
watch(isReceivedCheckStatus, (newValue, oldValue) => {
  // 부모에서 상태 확인이 시작되면 (0으로 리셋되는 경우) 로딩 상태로 설정
  if (newValue === 0 && oldValue !== 0 && oldValue !== undefined) {
    status.value = 1;
    // 3초 타임아웃 설정
    if (timerId.value) {
      clearTimeout(timerId.value);
    }
    timerId.value = setTimeout(() => {
      if (status.value === 1) {
        status.value = 3; // 오프라인으로 설정
        store.dispatch("home/updateCheckControllers", { serial: mqttControllerId.value, value: 3 });
        emit('status-changed', { status: 3, controllerId: props.controllerId });
      }
    }, 3000);
  }

  // 스토어에 오프라인이 기록되면 UI도 동기화
  if (newValue === 3 && status.value !== 3) {
    status.value = 3;
    if (timerId.value) {
      clearTimeout(timerId.value);
      timerId.value = undefined;
    }
    emit('status-changed', { status: 3, controllerId: props.controllerId });
  }
  
  // 응답이 오면 온라인으로 설정 (checkStatus가 1이거나 3일 때 모두 처리)
  if (newValue === 1 && (status.value === 1 || status.value === 3 || status.value === 0)) {
    status.value = 2;
    if (timerId.value) {
      clearTimeout(timerId.value);
      timerId.value = undefined;
    }
    emit('status-changed', { status: 2, controllerId: props.controllerId });
  }
});

// Load schedules for temp controllers on mount
onMounted(async () => {
  if (boilerDevice.value?._id) {
    try {
      const result = await Axios.get(ScheduleApi.SCHEDULES, { deviceId: boilerDevice.value._id });
      if (result.resultCode === '0000') {
        schedules.value = result.resultData || [];
      }
    } catch (error) {
      console.log(error);
    }
  }
  
  // Check initial state from store
  const initialStoreStatus = isReceivedCheckStatus.value;
  if (initialStoreStatus === 0) {
    // Store shows checking in progress, set status to checking
    status.value = 1;
    if (timerId.value) {
      clearTimeout(timerId.value);
    }
    timerId.value = setTimeout(() => {
      if (status.value === 1) {
        status.value = 3; // 오프라인으로 설정
        store.dispatch("home/updateCheckControllers", { serial: mqttControllerId.value, value: 3 });
        emit('status-changed', { status: 3, controllerId: props.controllerId });
      }
    }, 3000);
  } else if (initialStoreStatus === 1) {
    // Store shows online
    status.value = 2;
    emit('status-changed', { status: 2, controllerId: props.controllerId });
  } else if (initialStoreStatus === 3) {
    status.value = 3;
    emit('status-changed', { status: 3, controllerId: props.controllerId });
  }
  
  // Auto-check if enabled
  if (props.autoCheck) {
    checkStatus();
  }
});

// Cleanup on unmount
onBeforeUnmount(() => {
  if (timerId.value) {
    clearTimeout(timerId.value);
    timerId.value = undefined;
  }
});

// Check controller status
const checkStatus = async () => {
  // Reset status in store
  store.dispatch("home/updateCheckControllers", { serial: mqttControllerId.value, value: 0 });
  
  // Set checking state
  status.value = 1;
  emit('status-changed', { status: 1, controllerId: props.controllerId });
  
  // Send MQTT request based on controller type
  if (isTempController.value) {
    // Temp controller - use schedules
    const value = schedules.value?.length > 0 ? (schedules.value[0].isOn ? '1' : '0') : '0';
    mqttControl.onPublishTemp(mqttControllerId.value, MQTTTempCommand.TimerGroupEnable1, value);
  } else {
    // Light controller
    mqttControl.onPublishController(props.controllerId, {
      serial: props.controllerId,
      cmd: 'p',
      type: 1,
      data: {
        ports: [1],
        requestId: 1
      }
    });
  }
  
  // Set timeout for offline detection
  if (timerId.value) {
    clearTimeout(timerId.value);
  }
  timerId.value = setTimeout(() => {
    if (status.value === 1) {
      status.value = 3; // 오프라인으로 설정
      store.dispatch("home/updateCheckControllers", { serial: mqttControllerId.value, value: 3 });
      emit('status-changed', { status: 3, controllerId: props.controllerId });
    }
  }, 3000);
};

// Refresh button click handler
const onRefreshClick = () => {
  if (status.value === 1) {
    return; // Already checking
  }
  checkStatus();
};

// Expose method for parent components
defineExpose({
  checkStatus,
});
</script>

<style scoped>
.controller-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.controller-status-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  height: 1.25rem;
  flex-shrink: 0;
}

.controller-status-icon .spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

.controller-status-icon i {
  font-size: 1rem;
  display: inline-block;
  line-height: 1;
}

.controller-status-icon .fa-wifi {
  color: #28a745 !important;
}

.controller-status-icon .controller-status-offline-icon {
  color: #dc3545 !important;
  font-size: 1rem !important;
  display: inline-block !important;
}

.controller-status-icon .fa-question {
  color: #6c757d;
}

.controller-status-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #212529;
  white-space: nowrap;
  flex-shrink: 0;
}

.controller-status-refresh {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  padding: 0;
  border: none;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  border-radius: 0.25rem;
}

.controller-status-refresh:hover:not(:disabled) {
  color: #0d6efd;
  background-color: #f0f0f0;
}

.controller-status-refresh:active:not(:disabled) {
  transform: scale(0.95);
}

.controller-status-refresh:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.controller-status-refresh i {
  font-size: 0.875rem;
}

.controller-status-refresh i.fa-spin {
  animation: fa-spin 1s infinite linear;
}

/* 모바일 반응형 */
@media (max-width: 768px) {
  .controller-status {
    gap: 0.375rem;
  }

  .controller-status-text {
    font-size: 0.8rem;
  }

  .controller-status-icon {
    width: 1.1rem;
    height: 1.1rem;
  }

  .controller-status-refresh {
    width: 1.4rem;
    height: 1.4rem;
  }
}
</style>
